import {
  deleteProjectUpvote,
  upvoteProject,
} from '@/data/services/projectServices';
import { projectsQuerykeys } from '@/data/services/querykeys';
import { Project } from '@/models/Project';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

export function useUpvoting(
  project: Project,
  onMutate?: (project: Project) => void,
) {
  const { upVoted, upVotes, id } = project;

  const onUpvote = useCallback(async () => {
    if (upVoted) {
      await deleteProjectUpvote(id);
      return upVotes - 1;
    } else {
      await upvoteProject(id);
      return upVotes + 1;
    }
  }, [id, upVoted, upVotes]);
  const queryClient = useQueryClient();
  const { mutate: upVote, isPending: isUpvoting } = useMutation(onUpvote, {
    async onSuccess(upVotes) {
      onMutate?.({ ...project, upVoted: !upVoted, upVotes });
      await queryClient.invalidateQueries(projectsQuerykeys.get(project.id));
      await queryClient.invalidateQueries({
        queryKey: projectsQuerykeys.list._def,
      });
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  return { upVote, isUpvoting };
}

export const useVoteStyles = (upVoted: boolean) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'projects.card.actions',
  });
  const variants = {
    unvote: {
      color: 'text-error',
      text: t('unvote'),
      Icon: XMarkIcon,
    },
    vote: {
      color: 'text-primary',
      text: t('vote'),
      Icon: CheckIcon,
    },
  };
  return variants[upVoted ? 'unvote' : 'vote'];
};
